package com.elan.edubridge.shared.domain.model

import kotlinx.serialization.Serializable

/**
 * 공지·게시판 계약. `functions/src/notices.ts`·`functions/src/board.ts`의 콜러블 10종에 대응한다.
 *
 * **학원당 하나씩이다.** 공지 피드도 게시판도 `edubridge_academies/{academyId}` 바로 아래에 있고
 * `boards/{boardId}` 같은 중간 계층이 없다 — 그래서 모든 요청이 `academyId`만으로 대상을 특정한다.
 * **댓글은 게시글에만 달린다** — 공지에는 댓글 경로 자체가 없다.
 *
 * **소프트 삭제**: 삭제 콜러블은 문서를 지우지 않고 [ContentStatus.DELETED] 툼스톤으로 바꾼다.
 * 본문·제목은 서버가 같은 쓰기에서 비우지만 문서는 남아 rules를 통과하므로, 목록을 그리는 쪽은
 * 반드시 ACTIVE만 걸러야 한다 — 그 필터는 `EduConnectRepositoryImpl` 한 곳에 있다.
 */

/**
 * 공지·게시글·댓글의 수명 상태. 세 컬렉션이 같은 문자열 값을 쓴다.
 *
 * 매핑 기본값은 [DELETED]가 아니라 [ACTIVE]다 — 서버가 이 필드를 항상 채워 쓰므로 값이 없는
 * 문서는 이 계약 이전에 만들어진 것뿐이고, 그것들은 삭제된 적이 없다.
 */
@Serializable
enum class ContentStatus { ACTIVE, DELETED }

/**
 * 첨부 허용 MIME. `board.ts`의 `ATTACHMENT_CONTENT_TYPES` allowlist와 1:1이다.
 *
 * 열거형인 이유는 업로드 요청이 이 넷 중 하나여야 서버 `requireEnum`을 통과하기 때문이다 —
 * 자유 문자열이면 화면이 `invalid-argument`를 받고 나서야 실수를 안다.
 */
@Serializable
enum class AttachmentContentType(val mimeType: String) {
    JPEG("image/jpeg"),
    PNG("image/png"),
    WEBP("image/webp"),
    PDF("application/pdf"),
    ;

    companion object {
        /** 서버가 저장한 MIME 문자열 -> 열거값. allowlist에 없는 값이면 null이다. */
        fun fromMimeType(value: String?): AttachmentContentType? =
            entries.firstOrNull { it.mimeType == value }
    }
}

/**
 * 게시글 문서가 들고 있는 첨부 항목(`posts/{postId}.attachments` 배열의 원소).
 *
 * 목록 화면이 첨부 유무·개수를 그리는 데 추가 호출이 필요 없도록 서버가 게시글 안에 인라인해 둔다.
 * 실제 파일을 여는 것은 별개다 — `getPostAttachmentUrl`로 그때그때 서명 URL을 받아야 한다.
 *
 * [contentType]이 null이면 서버 allowlist가 클라이언트보다 먼저 늘어난 경우다. 미리보기를 시도하지
 * 말고 파일명만 보여 주는 쪽이 안전하다.
 */
@Serializable
data class PostAttachment(
    val id: String,
    val fileName: String,
    val contentType: AttachmentContentType? = null,
    val sizeBytes: Int = 0,
    /** Storage 오브젝트 경로. 클라이언트가 직접 열 수는 없고 서명 URL 발급의 근거다. */
    val objectPath: String,
    val createdAt: String? = null,
    val createdBy: String? = null,
)

/**
 * `edubridge_academies/{academyId}/posts/{id}` 읽기 모델.
 *
 * [commentCount]는 **읽을 수 있는 댓글 수**다 — 서버가 댓글 삭제 시 함께 감산하므로 툼스톤은
 * 세지 않는다([PostComment] 목록을 ACTIVE로 거른 크기와 일치한다).
 */
@Serializable
data class PostData(
    val id: String,
    val academyId: String,
    val title: String,
    val content: String,
    val status: ContentStatus = ContentStatus.ACTIVE,
    val authorUid: String? = null,
    /** 작성 시점의 표시 이름 스냅샷. 계정에 이름이 없으면 서버가 null로 쓴다. */
    val authorName: String? = null,
    val commentCount: Int = 0,
    val attachments: List<PostAttachment> = emptyList(),
    val createdAt: String = "",
    val updatedAt: String? = null,
)

/** `posts/{postId}/comments/{id}` 읽기 모델. 댓글은 게시글에만 달린다. */
@Serializable
data class PostComment(
    val id: String,
    val postId: String,
    val academyId: String,
    val content: String,
    val status: ContentStatus = ContentStatus.ACTIVE,
    val authorUid: String? = null,
    val authorName: String? = null,
    val createdAt: String = "",
)

/**
 * 콜러블이 커밋 후 시도한 푸시 팬아웃 결과.
 *
 * **실패해도 쓰기는 성공이다** — 공지는 이미 발행돼 읽히므로 전송 실패가 생성 실패로 올라오지
 * 않는다. 숫자가 0이어도 재시도를 유도하지 말 것.
 *
 * [truncated]는 공지 전용이다. 대상이 서버 상한(500명)을 넘어 일부에게만 전송된 경우이며,
 * 댓글 알림은 대상이 글쓴이 한 명뿐이라 항상 false다.
 */
@Serializable
data class PushDeliverySummary(
    val recipients: Int = 0,
    val installations: Int = 0,
    val delivered: Int = 0,
    val truncated: Boolean = false,
)

// ---- 콜러블 입력 ----
//
// 길이 제한은 서버(`notices.ts`/`board.ts`)가 강제한다. 여기 주석의 범위를 넘기면
// `INVALID_ARGUMENT`로 돌아온다.

/** 제목 1~100자, 본문 1~5000자. 호출자는 EDITOR 이상이어야 한다. */
@Serializable
data class CreateNoticeRequest(
    val academyId: String,
    val title: String,
    /** 평문. [contentHtml]을 보내면 서버가 그것에서 평문을 다시 뽑으므로 이 값은 무시된다. */
    val content: String,
    /** 에디터 출력(HTML). 서버가 살균해 저장한다 — 클라이언트는 손대지 않고 그대로 보낸다. */
    val contentHtml: String? = null,
)

/**
 * 공지 수정. [title]·[content] 중 **적어도 하나**는 null이 아니어야 한다 —
 * 둘 다 비우면 서버가 `INVALID_ARGUMENT`로 거부한다. null인 항목은 전송하지 않는다.
 */
@Serializable
data class UpdateNoticeRequest(
    val academyId: String,
    val noticeId: String,
    val title: String? = null,
    val content: String? = null,
    /** 서식 본문. 이것을 보내면 [content]는 서버가 다시 뽑는다. 평문만 보내면 서버가 서식을 지운다. */
    val contentHtml: String? = null,
)

/** 제목 1~100자, 본문 1~5000자. 호출자는 EDITOR 이상이어야 한다. */
@Serializable
data class CreatePostRequest(
    val academyId: String,
    val title: String,
    val content: String,
)

/** 게시글 수정. [UpdateNoticeRequest]와 같은 규칙 — 둘 다 null이면 거부된다. */
@Serializable
data class UpdatePostRequest(
    val academyId: String,
    val postId: String,
    val title: String? = null,
    val content: String? = null,
)

/**
 * 댓글 작성(1~1000자).
 *
 * **게시판에서 비스태프에게 열린 유일한 쓰기다** — 권한(EDITOR)이 아니라 ACTIVE 멤버십만 본다.
 */
@Serializable
data class CreateCommentRequest(
    val academyId: String,
    val postId: String,
    val content: String,
)

/**
 * 첨부 업로드 티켓 발급 요청. 파일명 1~200자, 크기 1~10485760바이트(10MiB),
 * [contentType]은 allowlist 4종, 게시글당 5개까지다.
 *
 * [sizeBytes]는 서명에 묶이지 않아 서버가 강제할 수 없다 — 선언·기록될 뿐이다.
 */
@Serializable
data class CreatePostAttachmentUploadRequest(
    val academyId: String,
    val postId: String,
    val fileName: String,
    val contentType: AttachmentContentType,
    val sizeBytes: Int,
)

// ---- 콜러블 결과 ----
//
// 실패는 예외가 아니라 값이다(`CommandModels.kt` 참고). 수정·삭제 5종은 돌려줄 상태가 id뿐이라
// 공용 `CommandResult`를 쓰고, 새 문서를 만드는 3종과 첨부 2종만 전용 결과형을 갖는다.

/** `createNotice` 결과. 발행된 공지와 그때 시도한 팬아웃 결과를 함께 돌려준다. */
@Serializable
data class NoticeResult(
    val notice: NoticeData? = null,
    val notified: PushDeliverySummary? = null,
    val failure: CommandFailure? = null,
    val errorMessage: String? = null,
) {
    val isSuccess: Boolean get() = failure == null && notice != null
}

/** `createPost` 결과. 첨부는 아직 없으므로 [PostData.attachments]는 항상 비어 있다. */
@Serializable
data class PostResult(
    val post: PostData? = null,
    val failure: CommandFailure? = null,
    val errorMessage: String? = null,
) {
    val isSuccess: Boolean get() = failure == null && post != null
}

/** `createComment` 결과. [notified]는 글쓴이 1명 대상이고 본인 댓글이면 0건이다. */
@Serializable
data class CommentResult(
    val comment: PostComment? = null,
    val notified: PushDeliverySummary? = null,
    val failure: CommandFailure? = null,
    val errorMessage: String? = null,
) {
    val isSuccess: Boolean get() = failure == null && comment != null
}

/**
 * `createPostAttachmentUploadUrl` 결과 — 첨부 예약 + 10분짜리 V4 서명 PUT URL.
 *
 * **Storage SDK 간편 업로드를 쓰면 안 된다.** `storage.rules`가 `edubridge/` 아래를 전면 차단하고
 * 있어서 이 서명 URL이 유일한 쓰기 경로다. 클라이언트는 [uploadUrl]에 **평범한 HTTP PUT**으로
 * 파일 바이트를 보내되 [requiredHeaders]를 그대로 실어야 한다 — `Content-Type`이 서명에 묶여
 * 있어 다른 값을 보내면 GCS가 거부한다.
 *
 * 예약은 PUT 성공 여부와 무관하게 게시글에 남는다(서버는 업로드 완료를 통보받지 않는다).
 */
@Serializable
data class PostAttachmentUploadTicket(
    val attachmentId: String? = null,
    val academyId: String? = null,
    val postId: String? = null,
    val fileName: String? = null,
    val contentType: AttachmentContentType? = null,
    val sizeBytes: Int = 0,
    val objectPath: String? = null,
    val uploadUrl: String? = null,
    /** PUT에 **그대로** 실어야 하는 헤더. 지금은 `Content-Type` 하나다. */
    val requiredHeaders: Map<String, String> = emptyMap(),
    /** ISO-8601. 발급 후 10분. */
    val expiresAt: String? = null,
    val failure: CommandFailure? = null,
    val errorMessage: String? = null,
) {
    val isSuccess: Boolean get() = failure == null && uploadUrl != null
}

/**
 * `getPostAttachmentUrl` 결과 — 5분짜리 V4 서명 GET URL.
 *
 * 매번 새로 발급받아야 한다. 삭제된 게시글은 첨부 목록이 비워지므로 `NOT_FOUND`로 돌아온다.
 */
@Serializable
data class PostAttachmentDownloadTicket(
    val attachmentId: String? = null,
    val academyId: String? = null,
    val postId: String? = null,
    val fileName: String? = null,
    val contentType: AttachmentContentType? = null,
    val sizeBytes: Int = 0,
    val objectPath: String? = null,
    val downloadUrl: String? = null,
    /** ISO-8601. 발급 후 5분. */
    val expiresAt: String? = null,
    val failure: CommandFailure? = null,
    val errorMessage: String? = null,
) {
    val isSuccess: Boolean get() = failure == null && downloadUrl != null
}
